// Layer 4 (UI) — the Process route's small glass pieces, per the locked comp
// `.impeccable/mocks/decision/process.html` A: the card (title, inline caption,
// meta and actions inside), the mode / status chip in the chip recipe, the
// ink-filled answer buttons and the selected reading's control chart with its
// legend. Presentational only; every value arrives from Scene.transform.ts.

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';
import { ControlChart } from '@/versions/v1/modules/_viz';
import type { ProcessChart, ProcessTone } from './Scene.transform';

const CHIP_BASE =
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]';

const CHIP_TONE: Record<ProcessTone, string> = {
  warn: 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)] text-[var(--state-warning)]',
  info: 'bg-[color-mix(in_srgb,var(--state-info)_12%,transparent)] text-[var(--state-info)]',
  ok: 'bg-[color-mix(in_srgb,var(--state-online)_12%,transparent)] text-[var(--state-online)]',
  bad: 'bg-[color-mix(in_srgb,var(--state-blocked)_12%,transparent)] text-[var(--state-blocked)] dark:bg-[color-mix(in_srgb,var(--state-blocked)_22%,transparent)]',
  neutral: 'bg-[var(--surface-3)] text-ink-secondary shadow-[inset_0_0_0_1px_var(--hairline)]',
};

/** A mode or status chip: colour only for the exception (manual past the rule,
 *  a maintenance hold, an out-of-range reading); Auto is a quiet neutral chip. */
export function ToneChip({ tone, children, ...rest }: { tone: ProcessTone; children: ReactNode } & Record<`data-${string}`, string>) {
  return (
    <span className={cn(CHIP_BASE, CHIP_TONE[tone])} {...rest}>
      {children}
    </span>
  );
}

/** An in-band status is quiet (cross-route ruling: RUN and in-band states are
 *  a tick, only exceptions take colour). */
export function StatusMark({ status, tone }: { status: string; tone: ProcessTone }) {
  if (tone === 'ok') {
    return (
      <span data-status={status} className="inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-semibold uppercase leading-4 tracking-[0.05em] text-muted">
        <svg viewBox="0 0 10 10" className="size-2.5" aria-hidden>
          <path d="M2 5.2 4.1 7.3 8 2.9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {status}
      </span>
    );
  }
  return <ToneChip tone={tone} data-status={status}>{status}</ToneChip>;
}

/** The comp's `.card`: a matte card with its title inside, an optional muted
 *  caption beside the title, meta and actions on the right. */
export function ProcessCard({
  title,
  caption,
  meta,
  actions,
  className,
  children,
  ...rest
}: {
  title: ReactNode;
  caption?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
} & Record<`data-${string}`, string>) {
  return (
    <GlassCard as="section" tone="matte" className={cn('min-w-0 px-4 py-3', className)} {...rest}>
      <header className="mb-2.5 flex min-w-0 flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-ink-primary">{title}</h3>
        {caption ? <span className="text-xs text-muted">{caption}</span> : null}
        <span className="flex-1" />
        {meta ? <span className="text-xs tabular-nums text-muted">{meta}</span> : null}
        {actions}
      </header>
      {children}
    </GlassCard>
  );
}

/** A caps sub-label inside a card (`Matched rule`, `Action`, `Held by`). */
export function SubLabel({ children, end }: { children: ReactNode; end?: ReactNode }) {
  return (
    <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.07em] text-muted">
      <span>{children}</span>
      {end ? <span className="ml-auto">{end}</span> : null}
    </div>
  );
}

/** Primary actions are ink-filled (DESIGN.md § Crimson: crimson never fills a
 *  button); the secondary answer is a hairline button beside it. */
export function AnswerButton({ primary = false, className, ...rest }: { primary?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-8 items-center rounded-[10px] border px-3 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]',
        primary
          ? 'border-ink-primary bg-ink-primary text-[var(--surface-2)] hover:opacity-90'
          : 'border-[var(--hairline-strong)] bg-[var(--surface-2)] text-ink-secondary hover:bg-[var(--surface-1)] hover:text-ink-primary',
        className,
      )}
      {...rest}
    />
  );
}

function LegendSwatch({ kind }: { kind: 'mean' | 'limits' | 'beyond' | 'shade' }) {
  if (kind === 'beyond') return <span aria-hidden className="inline-block size-[7px] rounded-full bg-[var(--state-blocked)]" />;
  if (kind === 'shade') return <span aria-hidden className="inline-block h-2 w-2.5 bg-[color-mix(in_srgb,var(--state-warning)_18%,transparent)]" />;
  return <span aria-hidden className={cn('inline-block w-3 border-t-[1.5px] border-[var(--ink-tertiary)]', kind === 'limits' && 'border-dashed')} />;
}

/** The selected variable's control chart: the shared `ControlChart` over the
 *  same `controlChart()` object Plant model draws, its caption, and a legend
 *  for the marks this chart carries. */
export function ProcessChartBlock({ chart, title }: { chart: ProcessChart; title?: ReactNode }) {
  return (
    <div data-process-chart={chart.variableId} className="min-w-0">
      <div className="mb-1.5 flex items-center gap-2 [&>div]:mb-0">
        {title ?? <SubLabel>Control chart</SubLabel>}
        <span className="flex-1" />
        <span className="text-xs tabular-nums text-muted">{chart.windowLabel}</span>
      </div>
      <ControlChart
        model={chart.model}
        height={164}
        hideLabel
        seriesTone="ink"
        pointMarkers="highlighted"
        unitLabelPosition="top"
        tickLabelPx={10}
        limitLabels
        minTimeTicks={4}
        timeTickDay="change"
      />
      {chart.beyond || chart.runRulesLabel ? (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] leading-tight text-muted" data-chart-legend>
          <span className="inline-flex items-center gap-1.5"><LegendSwatch kind="mean" />mean</span>
          <span className="inline-flex items-center gap-1.5"><LegendSwatch kind="limits" />control limits</span>
          {chart.beyond ? <span className="inline-flex items-center gap-1.5"><LegendSwatch kind="beyond" />beyond limit</span> : null}
          {chart.runRulesLabel ? <span className="inline-flex items-center gap-1.5"><LegendSwatch kind="shade" />{chart.runRulesLabel}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
